import type { Ability, Section } from "@qendo/permissions";
import { type ReactNode, useMemo } from "react";
import { useProfile } from "~/providers/ProfileProvider";
import { Only, type PermissibleProps } from "./Only";

export type CanProps<S extends Section, A extends Ability<S>> = {
    ability: A | A[];
    section: S | S[];
    any?: boolean;
    not?: boolean;
    children?: ReactNode;
} & PermissibleProps;

/*
 * This has four modes, and will show children only if:
 *   - can: every combination of ability and section passes
 *   - can any: at least one combination of ability and section passes
 *   - cannot: at least one combination of ability and section fails
 *   - cannot any: every combination of ability and section fails
 */
export const Can = <S extends Section = Section, A extends Ability<S> = Ability<S>>({
    ability,
    section,
    any = false,
    not = false,
    children,
    ...onlyProps
}: CanProps<S, A>) => {
    const { can, cannot, canAny, cannotAny } = useProfile();

    const permitted = useMemo(
        () => (not ? (any ? cannotAny : cannot) : any ? canAny : can)(ability, section),
        [can, cannot, canAny, cannotAny, not, any, ability, section],
    );

    return (
        <Only {...onlyProps} when={permitted}>
            {children}
        </Only>
    );
};
